/**
 * Recovers a tab whose route chunk no longer exists on the server.
 *
 * Routes are code-split into content-hashed chunks that load on navigation, and deploys don't keep
 * every past build's chunks forever. A tab left open across a deploy can therefore ask for a filename
 * the origin no longer has; Vite raises `vite:preloadError` for that failed import. Reloading picks up
 * the current build's entry point and the navigation then succeeds — without this the user gets the
 * router's error screen on a route that works fine everywhere else.
 *
 * The event is deliberately left uncancelled. Vite rethrows the original error only when the event is
 * *not* cancelled — its handler ends in `baseModule().catch(handlePreloadError)` — so cancelling makes
 * a rejected import resolve with `undefined` instead. The router then dereferences that:
 * `lazyRouteComponent` runs `comp = res[exportName]` and throws `TypeError: Cannot read properties of
 * undefined (reading 'component')`, which buries the real chunk-load failure behind what looks like a
 * render bug and gets classified as one by `classifyRouteError`. Leaving the rejection intact keeps the
 * original message — fingerprinted as `chunk_load` — and lets the router's own module-not-found
 * recovery run.
 *
 * The reload is rate-limited through `sessionStorage`: a chunk that is genuinely broken rather than
 * merely stale would otherwise reload forever.
 *
 * Call once during app startup; returns a cleanup function.
 */
const RELOAD_ATTEMPT_KEY = 'liza.staleChunkReloadAt';
const RELOAD_COOLDOWN_MS = 10_000;

// Storage access throws rather than no-ops in some privacy modes and on devices out of disk, and a
// failure to read the guard must not stop the recovery (nor replace one error screen with another).
const readLastAttempt = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY)) || 0;
  } catch {
    return 0;
  }
};

const recordAttempt = (at: number): void => {
  try {
    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(at));
  } catch {
    // Unwritable storage means the cooldown can't be enforced; a single reload is still worth trying.
  }
};

export const reloadOnStaleChunkLoad = (): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handlePreloadError = (): void => {
    const now = Date.now();
    if (now - readLastAttempt() < RELOAD_COOLDOWN_MS) {
      return;
    }
    recordAttempt(now);
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', handlePreloadError);

  return () => window.removeEventListener('vite:preloadError', handlePreloadError);
};
